const STORAGE_KEY = 'algoliaKeys';

// Refresh before expiry to reduce the chance of requests failing due to an expiring key.
const DEFAULT_REFRESH_BUFFER_MS = 60 * 1000; // 1 minute

// If the backend does not provide expiry metadata, use a conservative default.
// Prefer server-provided `expiresAt`/`expiration`/`ttlSeconds` so the frontend does not have to guess.
const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Backoff to avoid hammering the API when refresh fails (offline, transient errors, etc.).
const BACKOFF_BASE_MS = 2 * 1000;
const BACKOFF_MAX_MS = 60 * 1000;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function jitter(ms, pct = 0.1) {
  const delta = ms * pct;
  return ms + (Math.random() * 2 - 1) * delta;
}

function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

/**
 * Extract an absolute expiration instant from the backend payload.
 *
 * Supported shapes:
 * - `ttlSeconds`: number (relative TTL in seconds)
 * - `expiresAt`: epoch ms (preferred), or a parseable date string
 * - `expiration`: legacy alias of `expiresAt`
 * - `validUntil`: Unix timestamp in seconds (backend field), epoch ms, or parseable date string
 *
 * Internally we operate on epoch milliseconds (UTC-safe) for comparisons/scheduling.
 */
function computeExpiresAtMs(keys) {
  if (!keys) return null;

  // Relative TTL (in seconds)
  if (typeof keys.ttlSeconds === 'number' && Number.isFinite(keys.ttlSeconds) && keys.ttlSeconds > 0) {
    return Date.now() + keys.ttlSeconds * 1000;
  }

  // Check multiple possible expiry field names (order matters: prefer explicit over legacy)
  const candidate = keys.expiresAt ?? keys.validUntil ?? keys.expiration;
  if (!candidate) return null;

  // If it's a number, determine if it's Unix seconds or epoch milliseconds
  // Unix timestamps are typically < 1e12 (before year 2286), epoch ms are >= 1e12
  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    // If it's a Unix timestamp (seconds), convert to milliseconds
    return candidate < 1e12 ? candidate * 1000 : candidate;
  }

  // Backward compatibility: parse a date string if provided.
  const asDate = new Date(candidate);
  const ms = asDate.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function normalizeKeys(rawKeys) {
  if (!rawKeys?.appId || !rawKeys?.searchApiKey) return null;
  const expiresAtMs = computeExpiresAtMs(rawKeys) ?? Date.now() + DEFAULT_TTL_MS;

  return {
    appId: rawKeys.appId,
    searchApiKey: rawKeys.searchApiKey,
    // Persist expiry as epoch milliseconds to avoid timezone/format ambiguity.
    expiresAt: expiresAtMs,
  };
}

class AlgoliaKeysManager {
  constructor({ fetcher, refreshBufferMs = DEFAULT_REFRESH_BUFFER_MS } = {}) {
    if (!fetcher) throw new Error('AlgoliaKeysManager requires a fetcher');
    this.fetcher = fetcher;
    this.refreshBufferMs = refreshBufferMs;

    this._keys = null;
    this._refreshPromise = null;
    this._timer = null;
    this._failCount = 0;

    this._onStorage = this._onStorage.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onOnline = this._onOnline.bind(this);

    this._loadFromStorage();
    this._installGlobalListeners();
    this._schedule();

    // If no keys in storage, attempt initial fetch (non-blocking)
    if (!this._keys) {
      this.ensureFresh({ force: true }).catch(() => {
        // Initial fetch failed; backoff retry is already scheduled in ensureFresh
      });
    }
  }

  _installGlobalListeners() {
    // Cross-tab sync: if another tab refreshes keys, adopt the new value.
    window.addEventListener('storage', this._onStorage);
    // If the user returns after being away/suspended, refresh if we're near expiry.
    window.addEventListener('focus', this._onFocus);
    // If we were offline, attempt refresh when connectivity returns.
    window.addEventListener('online', this._onOnline);
  }

  _onStorage(e) {
    if (e.key !== STORAGE_KEY) return;
    this._loadFromStorage();
    this._schedule();
  }

  _onFocus() {
    // Avoid an unnecessary refresh, but ensure we won't expire while the user is active.
    this.ensureFresh().catch(() => {});
  }

  _onOnline() {
    this.ensureFresh({ force: true }).catch(() => {});
  }

  _loadFromStorage() {
    const parsed = safeJsonParse(localStorage.getItem(STORAGE_KEY));
    const normalized = normalizeKeys(parsed);
    // Don't load expired keys from storage - they're invalid
    if (normalized && normalized.expiresAt && normalized.expiresAt <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      this._keys = null;
    } else {
      this._keys = normalized;
    }
  }

  _saveToStorage(keys) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  }

  _clearTimer() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  _schedule() {
    this._clearTimer();
    if (!this._keys?.expiresAt) return;

    const refreshAt = this._keys.expiresAt - this.refreshBufferMs;
    const delayMs = refreshAt - Date.now();

    // If the refresh time has already passed, refresh immediately
    if (delayMs <= 0) {
      this.ensureFresh({ force: true }).catch(() => {});
      return;
    }

    // Clamp to reasonable bounds (0 to 24 hours)
    const clampedDelay = clamp(delayMs, 0, 24 * 60 * 60 * 1000);

    // Add small jitter so many clients don't refresh at the exact same moment.
    const delayWithJitter = Math.max(0, Math.floor(jitter(clampedDelay, 0.05)));

    this._timer = setTimeout(() => {
      this.ensureFresh({ force: true }).catch(() => {});
    }, delayWithJitter);
  }

  _isFreshEnough(keys = this._keys) {
    if (!keys?.expiresAt) return false;
    const now = Date.now();
    // Keys must not be expired AND have enough buffer time remaining
    return keys.expiresAt > now && keys.expiresAt - now > this.refreshBufferMs;
  }

  _isExpired(keys = this._keys) {
    if (!keys?.expiresAt) return true;
    return keys.expiresAt <= Date.now();
  }

  async ensureFresh({ force = false } = {}) {
    // Dedupe concurrent refreshes so multiple callers share one network request.
    if (this._refreshPromise) return this._refreshPromise;

    // If we already have a sufficiently fresh key, just return it.
    if (!force && this._isFreshEnough()) return this._keys;

    this._refreshPromise = (async () => {
      try {
        const responseKeys = await this.fetcher();
        if (!responseKeys) {
          // Fetcher returned null (e.g., API server URL not configured yet, or network error)
          // Schedule a retry with backoff instead of throwing
          this._failCount += 1;
          const backoff = clamp(BACKOFF_BASE_MS * 2 ** (this._failCount - 1), BACKOFF_BASE_MS, BACKOFF_MAX_MS);
          const retryIn = Math.floor(jitter(backoff, 0.2));
          this._clearTimer();
          this._timer = setTimeout(() => {
            this.ensureFresh({ force: true }).catch(() => {});
          }, retryIn);

          // If we have existing keys that aren't expired, return them; otherwise return null
          // Never return expired keys even if refresh fails
          return !this._isExpired() ? this._keys : null;
        }

        const normalized = normalizeKeys(responseKeys);
        if (!normalized) throw new Error('Algolia keys fetch returned invalid payload');

        this._keys = normalized;
        this._saveToStorage(normalized);
        this._failCount = 0;
        this._schedule();
        return normalized;
      } catch (err) {
        // Schedule a retry with backoff to avoid tight failure loops.
        this._failCount += 1;
        const backoff = clamp(BACKOFF_BASE_MS * 2 ** (this._failCount - 1), BACKOFF_BASE_MS, BACKOFF_MAX_MS);
        const retryIn = Math.floor(jitter(backoff, 0.2));
        this._clearTimer();
        this._timer = setTimeout(() => {
          this.ensureFresh({ force: true }).catch(() => {});
        }, retryIn);
        throw err;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }

  async getKeys() {
    // If keys are fresh enough, return them immediately
    if (this._isFreshEnough()) return this._keys;

    // If existing keys are expired, don't use them even if refresh fails
    const existingKeysExpired = this._isExpired();

    // Try to get fresh keys
    // If ensureFresh fails, it schedules a retry with exponential backoff via timer.
    // Don't retry here immediately - that would create duplicate requests and defeat the backoff strategy.
    const freshKeys = await this.ensureFresh({ force: true });

    // Never return expired keys - if refresh failed and existing keys are expired, return null
    if (!freshKeys && existingKeysExpired) {
      return null;
    }

    // If ensureFresh returned null and we have no keys, it has scheduled a retry via timer.
    // Return null here and let the scheduled retry handle it. The caller should handle null appropriately.
    if (!freshKeys && !this._keys) {
      return null;
    }

    // Return fresh keys if available, otherwise existing keys (if not expired), or null
    return freshKeys || (!existingKeysExpired ? this._keys : null);
  }
}

export default AlgoliaKeysManager;


